import PropTypes from "prop-types";
import React from "react";

import { classNames } from "@swa-ui/string";

import { Button } from "../Button";
import { Icon, iconPropTypes } from "../Icon";
import styles from "./OptionListItem.module.scss";

/**
 * OptionListItem renders children with a fixed suffix icon, and an optional prefix icon. This component
 * is to be primarily used within OptionList.
 */
export const OptionListItem = (props) => {
  const { children, className, onClick, prefixIcon = {}, suffixIcon = {}, title } = props;

  return (
    <Button {...getProps()}>
      <div className={styles.titleContainer}>
        <div className={styles.prefixFlexContainer}>
          {renderPrefixIcon()}
          {title}
        </div>
        {renderSuffixIcon()}
      </div>
      <div className={classNames(prefixIcon.name && styles.prefixFlexContainer)}>
        {renderPlaceholderPrefixIcon()}
        {children}
      </div>
    </Button>
  );

  function renderPrefixIcon() {
    const iconProps = {
      ...prefixIcon,
      className: classNames(styles.prefixIcon, prefixIcon?.className),
    };

    return prefixIcon.name && <Icon {...iconProps} />;
  }

  function renderPlaceholderPrefixIcon() {
    const iconProps = {
      ...prefixIcon,
      color: "transparent",
      name: "Bullet",
    };

    return prefixIcon.name && children && <Icon {...iconProps} />;
  }

  function renderSuffixIcon() {
    const iconProps = {
      name: "ArrowThinRight",
      ...suffixIcon,
      className: classNames(styles.suffixIcon, suffixIcon?.className),
    };

    return <Icon {...iconProps} />;
  }

  function getProps() {
    return {
      className: classNames(styles.optionListItem, className),
      fullWidth: true,
      onClick: handleClick,
      styleType: "no-style",
    };
  }

  function handleClick(event) {
    onClick?.(event);
  }
};

OptionListItem.displayName = "OptionListItem";

export const optionListItemPropTypes = {
  /** Content that will be rendered underneath the title. */
  children: PropTypes.node,

  /**
   * Additional classes for positioning the component. Given classes may only position this component for layout
   * purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** Callback event triggered when the OptionListItem is clicked on. */
  onClick: PropTypes.func,

  /** Props for the prefix icon. */
  prefixIcon: PropTypes.shape({ ...iconPropTypes, name: PropTypes.string }),

  /** Props for the suffix icon. By default, the suffix icon is a thin arrow pointing right. */
  suffixIcon: PropTypes.shape({ ...iconPropTypes, name: PropTypes.string }),

  /** Title of the option list item. */
  title: PropTypes.node.isRequired,
};

OptionListItem.propTypes = optionListItemPropTypes;
